import { useController, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import styles from "./FormPopup.module.scss";
import { useState } from "react";
import { activateReCAPTCHA, submitForm } from "@/components/forms/lib";
import { IFormFields } from "@/components/forms/interfaces/ISignUpForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

export const FormPopup: React.FC<{
  title?: string;
  subtitle?: string;
  // openState: ReturnType<typeof useState<boolean>>;
  openState: any;
  onClose?: () => void;
  onAccept?: () => void;
  leadSourceUTMCampaign?: string;
  hearXProductMulti?: string;
  downloadUrl: string;
  successMessage: string;
  detailsSubmitted?: boolean;
}> = ({
  title = "DOWNLOAD BROCHURE AND PRICING",
  subtitle = "Submit your details below to download our product brochure and find out about our pricing structure.",
  successMessage = "Thank you for submitting your details",
  openState: [isOpen, setOpen],
  onClose,
  onAccept,
  leadSourceUTMCampaign,
  downloadUrl,
  detailsSubmitted,
  hearXProductMulti,
}) => {
  const [downloadReady, setDownloadReady] = useState(false);
  const form = useForm<IFormFields>({
    defaultValues: {
      FIRST_NAME: "",
      LAST_NAME: "",
      EMAIL: "",
      LEAD_SOURCE_ID: 3292000,
      LEAD_DESCRIPTION: "",
      PHONE: "",
      ORGANISATION_NAME: "",
      CUSTOMFIELDS: [
        {
          FIELD_NAME: "Lead_Source_Secondary__c",
          FIELD_VALUE: "Website Product Enquiry",
        },
        {
          FIELD_NAME: "hearX_product_multi__c",
          FIELD_VALUE: hearXProductMulti || "Not Specified",
        },
        {
          FIELD_NAME: "Lead_Source_UTM_Campaign__c",
          FIELD_VALUE: "hearOAE launch brochure",
        },
        ...(leadSourceUTMCampaign
          ? [
              {
                FIELD_NAME: "Lead_Source_UTM_Campaign__c",
                FIELD_VALUE: leadSourceUTMCampaign,
              },
            ]
          : []),
        {
          FIELD_NAME: "Country_Code__c",
          FIELD_VALUE: "+1",
        },
      ],
    },
  });
  const close = () => {
    onClose && onClose();
    setOpen(false);
  };
  const accept = () => {
    onAccept && onAccept();
    setDownloadReady(false);
    setOpen(false);
  };
  const [apiError, setApiError] = useState<string>();
  const submit = (data: IFormFields) => {
    return new Promise((resolve, reject) => {
      submitForm(data, ({ error, field, responseData }) => {
        if (error) {
          if (field) {
            switch (field) {
              case "RE_CAPTCHA_NOT_FOUND":
              case "RE_CAPTCHA_NULL":
                setApiError("reCAPTCHA not found");
                break;
              case "RE_CAPTCHA":
                setApiError(
                  "There was an error with reCAPTCHA, please try again"
                );
                break;
              default:
                setApiError(field);
            }
          }
          reject(error);
        } else {
          resolve(responseData);
          setDownloadReady(true);
        }
      });
    });
  };

  const [reCAPTCHAActive, setReCAPTCHAActive] = useState(false);
  const handleReCAPTCHAActivation = () => {
    activateReCAPTCHA((active) => {
      setReCAPTCHAActive(active);
    });
  };

  return (
    <div
      className={classNames(styles.root, isOpen ? styles.open : styles.closed)}
    >
      <div className={styles.sheet}>
        {downloadReady || detailsSubmitted ? (
          <DownloadNow
            message={successMessage}
            onClick={accept}
            url={downloadUrl}
          />
        ) : (
          <>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.subtitle}>{subtitle}</p>
            <form
              className={styles.form}
              onSubmit={form.handleSubmit(submit)}
              onClick={() => {
                if (!reCAPTCHAActive) {
                  handleReCAPTCHAActivation();
                }
              }}
            >
              <div>
                <input
                  defaultValue=""
                  {...form.register("FIRST_NAME", {
                    required: "First name is required",
                    minLength: {
                      value: 2,
                      message: "First name must be at least 2 characters long",
                    },
                  })}
                  placeholder="First Name"
                />
                {form.formState.errors.FIRST_NAME && (
                  <span className={styles.error}>
                    {form.formState.errors.FIRST_NAME.message}
                  </span>
                )}
              </div>
              <div>
                <input
                  defaultValue=""
                  {...form.register("LAST_NAME", {
                    required: "Last name is required",
                    minLength: {
                      value: 2,
                      message: "Last name must be at least 2 characters long",
                    },
                  })}
                  placeholder="Last Name"
                />
                {form.formState.errors.LAST_NAME && (
                  <span className={styles.error}>
                    {form.formState.errors.LAST_NAME.message}
                  </span>
                )}
              </div>
              <div>
                <input
                  defaultValue=""
                  {...form.register("EMAIL", {
                    required: true,
                    pattern: {
                      value: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$/,
                      message: "Invalid email address",
                    },
                  })}
                  placeholder="Email"
                />
                {form.formState.errors.EMAIL && (
                  <span className={styles.error}>
                    {form.formState.errors.EMAIL.message}
                  </span>
                )}
              </div>
              <div>
                <ControlledPhoneInput form={form} />
                {form.formState.errors.PHONE && (
                  <span className={styles.error}>
                    {form.formState.errors.PHONE.message}
                  </span>
                )}
              </div>
              <div>
                <input
                  defaultValue=""
                  {...form.register("ORGANISATION_NAME", {
                    required: "Company is required",
                  })}
                  placeholder="Company"
                />
                {form.formState.errors.ORGANISATION_NAME && (
                  <span className={styles.error}>
                    {form.formState.errors.ORGANISATION_NAME.message}
                  </span>
                )}
              </div>
              {apiError && <span className={styles.error}>{apiError}</span>}
              <button type="submit">
                {form.formState.isSubmitting ? (
                  <FontAwesomeIcon
                    icon={faSpinner as IconProp}
                    spin={true}
                    className={styles.spinner}
                  />
                ) : (
                  "Download"
                )}
              </button>
            </form>
          </>
        )}
        <div className={styles.closeButton} onClick={close}>
          <FontAwesomeIcon icon={faTimes as IconProp} color="white" />
        </div>
      </div>
      <div className={styles.backdrop} onClick={close} />
    </div>
  );
};

export const ControlledPhoneInput: React.FC<{
  form: any;
}> = ({ form }) => {
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({
    control: form.control,
    name: "PHONE",
    rules: {
      required: "Phone number is required",
      minLength: {
        value: 10,
        message: "Phone number must be at least 10 characters long",
      },
    },
  });
  const onPhoneNumberChange = (
    number: string,
    countryData: {
      name: string;
      dialCode: string;
      countryCode: string;
      format: string;
    }
  ) => {
    onChange(`+${number}`);
    const countryCodeField = form
      .getValues("CUSTOMFIELDS")
      .find((field: any) => field.FIELD_NAME === "Country_Code__c");
    if (countryCodeField) {
      countryCodeField.FIELD_VALUE = `+${countryData.dialCode}`;
    }
  };

  return (
    <PhoneInput
      country={"us"}
      enableSearch={true}
      preferredCountries={["us", "za", "ca", "au", "gb"]}
      disableSearchIcon={true}
      placeholder={""}
      onChange={onPhoneNumberChange}
      onBlur={onBlur}
      value={value}
      inputProps={{ required: true }}
      countryCodeEditable={false}
      masks={{ za: ".. ... ...." }}
    />
  );
};

const DownloadNow: React.FC<{
  url: string;
  onClick: () => void;
  message: string;
}> = ({ url, onClick, message }) => {
  return (
    <div className={styles.downloadNow}>
      <h2 className={styles.title}>Thank you for submitting your details</h2>
      <p className={styles.subtitle}>{message}</p>

      <a href={url} target="_blank" rel="noreferrer noopener" onClick={onClick}>
        Download now
      </a>
    </div>
  );
};
